import React, { useState, useEffect } from 'react';
import {
  ShoppingBag, Clock, CircleCheck as CheckCircle, Package, Truck,
  User, MapPin, Calendar, RefreshCw, TriangleAlert as AlertTriangle,
  RotateCcw, MapPinned, CreditCard, Star, Loader
} from 'lucide-react';
import { trackFood, trackError } from '../Utils/analytics';
import { ORDER_HISTORY_URL, CART_ADD_URL, CART_VIEW_URL } from '../../config/api';
import { authenticatedFetch } from '../Utils/apiHelpers';
import OrderTrackingPanel from '../OrderTracking/OrderTrackingPanel';
import FoodReviewModal from '../FoodReviewModal/FoodReviewModal';
import './UserPlacedOrdersTab.css';

interface OrderItem {
  _id: string;
  order_item_id: number;
  order_id: number;
  food_id: number;
  food_name: string;
  quantity: number;
  price: number;
  subtotal: number;
  created_at: string;
}

interface PlacedOrder {
  order_id: number;
  kitchen_name: string;
  items: OrderItem[];
  total_quantity: number;
  subtotal: number;
  delivery_fee: number;
  tax_amount: number;
  grand_total: number;
  status: string;
  payment_status: string;
  created_at: string;
  delivery_type?: string;
  delivery_address?: string;
}

interface OrderHistoryResponse {
  success: boolean;
  page: number;
  limit: number;
  orders: PlacedOrder[];
}

interface UserPlacedOrdersTabProps {
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const PAGE_LIMIT = 20;
const TRACKABLE_STATUSES = new Set(['pending', 'confirmed', 'accepted', 'preparing', 'picked', 'on_the_way', 'ready']);

const statusOptions = [
  { value: 'pending',    label: 'Pending',    color: '#6c757d', Icon: Clock },
  { value: 'confirmed',  label: 'Confirmed',  color: '#17a2b8', Icon: CheckCircle },
  { value: 'accepted',   label: 'Accepted',   color: '#20c997', Icon: CheckCircle },
  { value: 'preparing',  label: 'Preparing',  color: '#ffc107', Icon: Package },
  { value: 'ready',      label: 'Ready',      color: '#fd7e14', Icon: Package },
  { value: 'completed',  label: 'Completed',  color: '#28a745', Icon: CheckCircle },
  { value: 'cancelled',  label: 'Cancelled',  color: '#6c757d', Icon: Clock },
  { value: 'rejected',   label: 'Rejected',   color: '#dc3545', Icon: Clock },
];

const getStatusMeta = (status: string) =>
  statusOptions.find(o => o.value === status.toLowerCase());

const UserPlacedOrdersTab: React.FC<UserPlacedOrdersTabProps> = ({ showNotification }) => {
  const [orders, setOrders] = useState<PlacedOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackingOrderId, setTrackingOrderId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<{ foodId: number; foodName: string } | null>(null);
  const [reorderingId, setReorderingId] = useState<number | null>(null);

  const fetchUserOrders = async (page: number = 1, append: boolean = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setError(null);
    }

    try {
      const response = await authenticatedFetch(ORDER_HISTORY_URL(page, PAGE_LIMIT), {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          setError('You do not have permission to view orders.');
        } else if (response.status === 404) {
          setError('No orders found. Start ordering some delicious meals!');
        } else {
          setError('Failed to fetch your orders. Please try again later.');
        }
        return;
      }

      const body: OrderHistoryResponse = await response.json();
      const fetched = body.orders ?? [];
      setHasMore(fetched.length === PAGE_LIMIT);

      if (append) {
        setOrders(prev => [...prev, ...fetched]);
      } else {
        setOrders(fetched);
        if (fetched.length === 0) {
          setError("You haven't placed any orders yet. Browse our featured meals to get started!");
        }
      }

    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      trackError('user_orders_fetch_failed', 'user_placed_orders_tab');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchUserOrders(1, false);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchUserOrders(nextPage, true);
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatDeliveryType = (type?: string): string => {
    if (!type) return 'Self Pickup';
    if (type === 'delivery_partner' || type === 'Delivery_partner' || type === 'kitchen_delivery') return 'Kitchen Delivery';
    if (type === 'self_pickup') return 'Self Pickup';
    return type;
  };

  const handleReorder = async (order: PlacedOrder) => {
    if (!order.items || order.items.length === 0) return;
    setReorderingId(order.order_id);
    let addedCount = 0;
    let skippedCount = 0;
    try {
      // Check existing cart for kitchen conflict
      const cartRes = await authenticatedFetch(CART_VIEW_URL);
      if (cartRes.ok) {
        const cartData = await cartRes.json();
        if (cartData.success && cartData.items?.length > 0) {
          // Cart has items — proceed anyway; the API will handle conflicts per item
        }
      }

      for (const item of order.items) {
        try {
          const res = await authenticatedFetch(CART_ADD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ food_id: item.food_id, quantity: item.quantity }),
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.success) {
            addedCount++;
          } else {
            skippedCount++;
          }
        } catch {
          skippedCount++;
        }
      }

      if (addedCount > 0) {
        showNotification('success', 'Items Added to Cart', `${addedCount} item${addedCount !== 1 ? 's' : ''} added to your cart${skippedCount > 0 ? `, ${skippedCount} skipped` : ''}.`);
        trackFood('food_ordered', order.kitchen_name || 'unknown');
      } else {
        showNotification('warning', 'Reorder Failed', 'Could not add items to cart. They may be unavailable or from a different kitchen.');
      }
    } catch {
      showNotification('error', 'Reorder Failed', 'Network error. Please try again.');
    } finally {
      setReorderingId(null);
    }
  };

  useEffect(() => {
    fetchUserOrders(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div className="user-profile-empty-state">
        <div className="user-profile-loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '1rem' }}></div>
        <p>Loading your orders...</p>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="user-profile-empty-state">
        <ShoppingBag size={60} />
        <p>{error}</p>
        <button
          onClick={handleRefresh}
          className="user-profile-edit-btn"
          style={{ marginTop: '1rem' }}
        >
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="user-placed-orders-container">
      <div className="user-placed-orders-header">
        <div className="user-orders-title-section">
          <h4>My Orders</h4>
          <p>Orders you have placed as a customer</p>
        </div>
        <button
          onClick={handleRefresh}
          className="user-profile-edit-btn"
          disabled={isLoading}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="user-profile-empty-state">
          <ShoppingBag size={60} />
          <p>You haven't placed any orders yet. Browse our featured meals to get started!</p>
        </div>
      ) : (
        <>
          <div className="user-orders-list">
            {orders.map(order => {
              const meta = getStatusMeta(order.status);
              const StatusIcon = meta?.Icon ?? Clock;
              const statusColor = meta?.color ?? '#6c757d';
              const statusLabel = meta?.label ?? order.status;

              return (
                <div key={order.order_id} className="user-order-card">
                  <div className="user-order-header">
                    <div className="user-order-id-section">
                      <h5>Order #{order.order_id}</h5>
                      <div
                        className="user-order-status-badge"
                        style={{ backgroundColor: statusColor, color: 'white' }}
                      >
                        <StatusIcon size={16} />
                        <span>{statusLabel}</span>
                      </div>
                    </div>
                    <div className="user-order-date">
                      <Calendar size={14} />
                      <span>{formatDate(order.created_at)}</span>
                    </div>
                  </div>

                  <div className="user-order-details-grid">
                    <div className="user-order-detail">
                      <span className="user-detail-label">Kitchen</span>
                      <span className="user-detail-value">{order.kitchen_name}</span>
                    </div>
                    <div className="user-order-detail">
                      <span className="user-detail-label">Items</span>
                      <span className="user-detail-value">{order.total_quantity}</span>
                    </div>
                    <div className="user-order-detail">
                      <span className="user-detail-label">Subtotal</span>
                      <span className="user-detail-value">£{order.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="user-order-detail">
                      <span className="user-detail-label">Tax</span>
                      <span className="user-detail-value">£{order.tax_amount.toFixed(2)}</span>
                    </div>
                    <div className="user-order-detail">
                      <span className="user-detail-label">Total</span>
                      <span className="user-detail-value price">£{order.grand_total.toFixed(2)}</span>
                    </div>
                    <div className="user-order-detail">
                      <span className="user-detail-label">Payment</span>
                      <span className="user-detail-value">
                        <CreditCard size={13} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                        <span style={{
                          fontSize: '0.75rem',
                          color: order.payment_status === 'paid' ? '#28a745' : '#6c757d',
                          fontWeight: 500,
                        }}>
                          {order.payment_status}
                        </span>
                      </span>
                    </div>
                    <div className="user-order-detail">
                      <span className="user-detail-label">Delivery</span>
                      <span className="user-detail-value">{formatDeliveryType(order.delivery_type)}</span>
                    </div>
                  </div>

                  {order.items && order.items.length > 0 && (
                    <div className="order-items-breakdown">
                      <span className="order-items-label">Items ordered:</span>
                      <ul className="order-items-list">
                        {order.items.map(item => (
                          <li key={item.order_item_id} className="order-item-row">
                            <span className="oi-name">{item.food_name}</span>
                            <span className="oi-qty">x{item.quantity}</span>
                            <span className="oi-price">£{item.subtotal.toFixed(2)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="user-delivery-info-section">
                    {order.delivery_address ? (
                      <div className="user-delivery-info">
                        <div className="user-delivery-type">
                          <Truck size={16} />
                          <span>Delivery to Address</span>
                        </div>
                        <div className="user-delivery-address">
                          <MapPin size={14} />
                          <span>{order.delivery_address}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="user-delivery-info">
                        <div className="user-delivery-type">
                          <User size={16} />
                          <span>Self Pickup</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="user-order-actions">
                    {TRACKABLE_STATUSES.has(order.status.toLowerCase()) && (
                      <button
                        onClick={() =>
                          setTrackingOrderId(prev => prev === order.order_id ? null : order.order_id)
                        }
                        className={`user-order-action-btn track${trackingOrderId === order.order_id ? ' track--active' : ''}`}
                      >
                        <MapPinned size={16} />
                        <span>{trackingOrderId === order.order_id ? 'Hide Tracking' : 'Track Order'}</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleReorder(order)}
                      className="user-order-action-btn reorder"
                      disabled={reorderingId === order.order_id}
                    >
                      {reorderingId === order.order_id
                        ? <Loader size={16} className="otp-spinning" />
                        : <RotateCcw size={16} />}
                      <span>Reorder</span>
                    </button>
                    {(order.status === 'completed' || order.status === 'delivered') && order.items && order.items.length > 0 && (
                      <button
                        onClick={() => setReviewTarget({ foodId: order.items[0].food_id, foodName: order.items[0].food_name })}
                        className="user-order-action-btn rate"
                      >
                        <Star size={16} />
                        <span>Rate Items</span>
                      </button>
                    )}
                  </div>

                  {trackingOrderId === order.order_id && (
                    <OrderTrackingPanel
                      orderId={order.order_id}
                      onClose={() => setTrackingOrderId(null)}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.5rem' }}>
              <button
                onClick={handleLoadMore}
                className="user-profile-edit-btn"
                disabled={isLoadingMore}
                style={{ minWidth: '160px' }}
              >
                {isLoadingMore ? (
                  <>
                    <div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }} />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} />
                    Load More Orders
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {error && orders.length > 0 && (
        <div className="user-placed-orders-error">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}

      {reviewTarget && (
        <FoodReviewModal
          foodId={reviewTarget.foodId}
          foodName={reviewTarget.foodName}
          onClose={() => setReviewTarget(null)}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};

export default UserPlacedOrdersTab;
